import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import PortalShell, { StatusPill } from "@/components/PortalShell";
import PhotoUploader from "@/components/PhotoUploader";
import { ClipboardList, CheckCircle2, MapPin, CalendarDays, ChevronLeft, ChevronRight, Home, Box } from "lucide-react";

const Card = ({ children, ...p }) => <div className="card-soft" style={{ padding: 22 }} {...p}>{children}</div>;
const fmtDate = (d) => { if (!d) return "No date set"; const dt = new Date(d); return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "2-digit", year: "2-digit" }); };
const extractMpId = (s) => { const t = (s || "").trim(); if (!t) return ""; const m = t.match(/[?&]m=([^&\s]+)/); if (m) return m[1]; return t.replace(/^https?:\/\/\S*\//, ""); };

export default function ContractorPortal() {
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [props, setProps] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/jobs"); setJobs(data); } catch (e) { toast.error(apiErr(e)); }
    try { const { data } = await api.get("/contractor/properties"); setProps(data); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upcoming = jobs.filter((j) => j.status !== "completed");
  const done = jobs.filter((j) => j.status === "completed");

  const nav = [
    { key: "jobs", label: "My jobs", icon: <ClipboardList size={16} />, badge: upcoming.length },
    { key: "properties", label: "My properties", icon: <Home size={16} />, badge: props.length },
    { key: "calendar", label: "Calendar", icon: <CalendarDays size={16} /> },
    { key: "completed", label: "Completed", icon: <CheckCircle2 size={16} /> },
  ];

  // group upcoming by scheduled date for an agenda / calendar view
  const groups = {};
  upcoming.forEach((j) => { const k = j.scheduled_date || "No date set"; (groups[k] = groups[k] || []).push(j); });
  const orderedKeys = Object.keys(groups).sort((a, b) => (a === "No date set" ? 1 : b === "No date set" ? -1 : a.localeCompare(b)));

  return (
    <PortalShell nav={nav} active={tab} onNav={setTab} title="Contractor portal">
      {tab === "jobs" && (
        <div style={{ display: "grid", gap: 20 }}>
          {upcoming.length === 0 && <Card><p style={{ color: "var(--muted)" }}>You have no jobs assigned right now. When Solvana assigns you a job it will appear here.</p></Card>}
          {orderedKeys.map((k) => (
            <div key={k}>
              <div className="lbl-m" style={{ color: "var(--honey)", display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><CalendarDays size={15} /> {fmtDate(k === "No date set" ? null : k)}</div>
              <div style={{ display: "grid", gap: 14 }}>
                {groups[k].map((j) => <JobCard key={j.job_id} j={j} reload={load} />)}
              </div>
            </div>
          ))}
        </div>
      )}
      {tab === "calendar" && <MonthCalendar jobs={jobs} />}
      {tab === "properties" && <MyProperties props={props} jobs={jobs} />}
      {tab === "completed" && (
        <div style={{ display: "grid", gap: 14 }}>
          {done.length === 0 && <Card><p style={{ color: "var(--muted)" }}>No completed jobs yet.</p></Card>}
          {done.map((j) => <JobCard key={j.job_id} j={j} reload={load} done />)}
        </div>
      )}
    </PortalShell>
  );
}

function MyProperties({ props, jobs }) {
  if (!props.length) return <Card data-testid="contractor-properties-empty"><p style={{ color: "var(--muted)" }}>No properties assigned to you yet. When Solvana gives you access to a villa it will appear here with its digital twin.</p></Card>;
  return (
    <div style={{ display: "grid", gap: 20 }}>
      {props.map((p) => {
        const mp = extractMpId(p.matterport_id);
        const pJobs = jobs.filter((j) => j.property_id === p.property_id && j.status !== "completed");
        return (
          <Card key={p.property_id} data-testid={`contractor-property-${p.property_id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <b style={{ fontSize: 17 }}>{p.name}</b>
                <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{p.ref ? `${p.ref} · ` : ""}{p.address}</div>
              </div>
              {p.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((p.name ? p.name + ", " : "") + p.address)}`} target="_blank" rel="noreferrer" data-testid={`property-map-${p.property_id}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--honey)", textDecoration: "none" }}><MapPin size={14} /> Directions</a>}
            </div>
            {p.access_notes && <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 8 }}><b>Access:</b> {p.access_notes}</div>}
            {mp ? (
              <div style={{ position: "relative", paddingTop: "56.25%", marginTop: 14, borderRadius: 8, overflow: "hidden", border: "1px solid var(--line)" }} data-testid={`property-twin-${p.property_id}`}>
                <iframe title={`${p.name} digital twin`} src={`https://my.matterport.com/show/?m=${mp}&play=1`} frameBorder="0" allowFullScreen allow="xr-spatial-tracking" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
              </div>
            ) : <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--muted)" }}><Box size={15} /> No digital twin linked for this property yet.</div>}
            {pJobs.length > 0 && (
              <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 12 }}>
                <div className="lbl-m" style={{ color: "var(--honey)", marginBottom: 8 }}>Your jobs here</div>
                <div style={{ display: "grid", gap: 6 }}>
                  {pJobs.map((j) => (
                    <div key={j.job_id} data-testid={`property-job-${j.job_id}`} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                      <span>{j.title}</span>
                      <span style={{ color: "var(--muted)", whiteSpace: "nowrap" }}>{fmtDate(j.scheduled_date)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function MonthCalendar({ jobs }) {
  const [cursor, setCursor] = useState(() => new Date());
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const startDay = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const iso = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const today = new Date().toISOString().slice(0, 10);
  const byDate = {};
  jobs.forEach((j) => { if (j.scheduled_date) (byDate[j.scheduled_date] = byDate[j.scheduled_date] || []).push(j); });
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthLabel = cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const shift = (n) => setCursor(new Date(year, month + n, 1));

  return (
    <Card data-testid="contractor-calendar">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <button onClick={() => shift(-1)} data-testid="cal-prev" style={{ background: "none", border: "1px solid var(--line)", borderRadius: 8, padding: 6, cursor: "pointer" }}><ChevronLeft size={16} /></button>
        <b className="font-serif-c" style={{ fontSize: 20 }}>{monthLabel}</b>
        <button onClick={() => shift(1)} data-testid="cal-next" style={{ background: "none", border: "1px solid var(--line)", borderRadius: 8, padding: 6, cursor: "pointer" }}><ChevronRight size={16} /></button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6 }}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="lbl-m" style={{ textAlign: "center", color: "var(--muted)", paddingBottom: 4 }}>{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const date = iso(d);
          const dayJobs = byDate[date] || [];
          const isToday = date === today;
          return (
            <div key={date} data-testid={dayJobs.length ? `cal-day-${date}` : undefined} style={{ minHeight: 78, border: "1px solid var(--line)", borderRadius: 8, padding: 6, background: isToday ? "var(--soft)" : "var(--paper)", outline: isToday ? "1px solid var(--honey)" : "none" }}>
              <div style={{ fontSize: 12, color: isToday ? "var(--honey)" : "var(--muted)", fontWeight: isToday ? 700 : 400, marginBottom: 4 }}>{d}</div>
              <div style={{ display: "grid", gap: 3 }}>
                {dayJobs.map((j) => (
                  <div key={j.job_id} title={`${j.title} · ${j.property_name}${j.scheduled_time ? " · " + j.scheduled_time : ""}`} style={{ fontSize: 10, lineHeight: 1.2, padding: "2px 4px", borderRadius: 4, background: j.status === "completed" ? "#e7ecdf" : "#f3e4cf", color: j.status === "completed" ? "#5B6440" : "#8a5a1f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {j.scheduled_time ? `${j.scheduled_time.slice(0, 3)} · ` : ""}{j.title}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 14, fontSize: 12, color: "var(--muted)" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: "#f3e4cf", display: "inline-block" }} /> Scheduled</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: "#e7ecdf", display: "inline-block" }} /> Completed</span>
      </div>
    </Card>
  );
}

function JobCard({ j, reload, done }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const complete = async () => {
    setBusy(true);
    try { await api.post(`/jobs/${j.job_id}/complete`, { completion_note: note, completion_photos: photos }); toast.success("Job signed off, the Solvana team has been notified"); setOpen(false); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="contractor-job-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
        <div>
          <b style={{ fontSize: 16 }}>{j.title}</b>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{j.property_ref ? `${j.property_ref} · ` : ""}{j.property_name}</div>
        </div>
        <StatusPill status={j.status === "completed" ? "completed" : "scheduled"} />
      </div>
      {j.description && <p style={{ fontSize: 14, color: "var(--ink)", margin: "10px 0" }}>{j.description}</p>}
      {j.property_address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((j.property_name ? j.property_name + ", " : "") + j.property_address)}`} target="_blank" rel="noreferrer" data-testid="job-map-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--honey)", textDecoration: "none" }}><MapPin size={14} /> {j.property_address}</a>}
      {j.property_access && !done && <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 6 }}><b>Access:</b> {j.property_access}</div>}
      {done ? (
        <div style={{ marginTop: 12, borderTop: "1px solid var(--line)", paddingTop: 12 }}>
          <div style={{ fontSize: 12, color: "#5B6440" }}>Signed off {j.completed_at ? new Date(j.completed_at).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" }) : ""}</div>
          {j.completion_note && <p style={{ fontSize: 14, marginTop: 6 }}>{j.completion_note}</p>}
          {j.completion_photos?.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              {j.completion_photos.map((ph, idx) => { const u = typeof ph === "string" ? ph : ph.url; return <img key={idx} src={u} alt="" style={{ width: 84, height: 63, objectFit: "cover", borderRadius: 3 }} />; })}
            </div>
          )}
        </div>
      ) : !open ? (
        <button className="btn-ink" style={{ marginTop: 14 }} onClick={() => setOpen(true)} data-testid="job-signoff-open">Mark job complete</button>
      ) : (
        <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 14, display: "grid", gap: 12, maxWidth: 560 }}>
          <textarea className="inp" rows={3} placeholder="What did you do? Any notes for the team." value={note} onChange={(e) => setNote(e.target.value)} data-testid="job-completion-note" />
          <PhotoUploader value={photos} onChange={setPhotos} label="Photos of the finished work (optional)" />
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn-ink" disabled={busy} onClick={complete} data-testid="job-signoff-submit">{busy ? "Saving…" : "Sign off & notify Solvana"}</button>
            <button className="btn-outline" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </Card>
  );
}
